import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ButtonToolbar, Col, Dropdown, Row, Table } from 'react-bootstrap'
import {
  faBooks,
  faChevronDown,
  faEllipsisV,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Input } from 'antd'
import { debounce } from 'lodash'
import { EnrolState, TypesRequests, CourseTypes } from 'enum'
import { IMainState } from 'store/states'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import StateEnrol from 'components/ui/state-enrol/StateEnrol'
import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import { AvatarSizes } from 'design-system/avatar/src/resources/Avatar.enum'
import Avatar from 'design-system/avatar'
import { useTranslationRankmi, joinKeys } from 'helpers/translations.helper'
import FastActions from 'components/ui/FastActions'
import { ContentContainer } from 'components/ui/content-container/ContentContainer'
import useMergeApplications from '../../hooks/useMergeApplications'
import ModalDetailsDNC from '../ModalDetailsDNC'
import { DownloadReport } from '../DropdownItems'
import * as fromEnrolActions from '../../../../store/actions/enrols.action'
import ModalDetailsLMS from './components/ModalDetailsLMS'
import styles from './AdminApplications.module.scss'

const ApplicationsTable: React.FC<any> = () => {
  const { t } = useTranslation()
  const { adminList, list } = useSelector(({ enrols }: IMainState) => enrols)
  const { permission } = useSelector(({ user }: IMainState) => user)
  const dispatch = useDispatch()
  const [search, setSearch] = useState('')
  const prefix = 'administration.applications'
  const prefixTable = joinKeys(prefix, 'table')
  const tTable = useTranslationRankmi({
    prefix: prefixTable,
  })
  const { admin } = useSelector(
    ({ requestOutsideCourses }: IMainState) => requestOutsideCourses
  )
  const [selected, setSelected] = useState<CourseTypes | null>(null)

  const isAdmin =
    permission.admin ||
    permission.superadmin ||
    (permission.moderator && permission.permissions.canRequest)

  const fastActions = [
    {
      label: t(`home.myTeam.filters.LMS`),
      value: CourseTypes.LMS,
    },
    {
      label: t(`home.myTeam.filters.DNC`),
      value: CourseTypes.DNC,
    },
  ]
  const enrols = isAdmin ? adminList : list
  const data = useMergeApplications({
    lms: enrols,
    dnc: admin,
    selected,
  })

  const [onDesktop, setOnDesktop] = useState(true)
  const windowsSize = useWindowSize()

  useEffect(() => {
    if (windowsSize.width)
      setOnDesktop(windowsSize.width < mobileWidth ? false : true)
  }, [windowsSize])

  const pendings = enrols.filter(({ stateAdmin, state }) =>
    isAdmin ? stateAdmin === EnrolState.Pending : state === EnrolState.Pending
  ).length
  let pendingTranslate = 'morePending'

  switch (pendings) {
    case 0:
      pendingTranslate = 'zeroPending'
      break

    case 1:
      pendingTranslate = 'onePending'
      break
  }

  const getList = useCallback(
    (value?: string) => {
      if (isAdmin) {
        dispatch(
          fromEnrolActions.Actions.enrolAdminListReload({
            q: { course_name_or_user_name_or_user_lastname_or_user_identifier_cont: value },
          })
        )
      } else {
        dispatch(
          fromEnrolActions.Actions.getEnrolList({
            q: { course_name_or_user_name_or_user_lastname_or_user_identifier_cont: value },
          })
        )
      }
    },
    [dispatch, isAdmin]
  )
  const getListDebounced = useMemo(() => debounce(getList, 1000), [getList])

  useEffect(() => {
    getListDebounced(search)
  }, [getListDebounced, search])

  return (
    <ContentContainer title={t('administration.applications.title')}>
      {onDesktop && (
        <Row>
          <Col>
            <ButtonToolbar className={`float-right ${styles.buttons}`}>
              <Dropdown
                id="dropdown-manage"
                className={styles.dropdownManage}
                alignRight
              >
                <Dropdown.Toggle id="dropdown-manage-toggle" bsPrefix="noCaret">
                  {t('administration.courses.list.dropdown.actions')}
                  <FontAwesomeIcon icon={faChevronDown} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <DownloadReport
                    type={
                      isAdmin
                        ? TypesRequests.applications
                        : TypesRequests.enrols
                    }
                    typeCourse={selected}
                  />
                </Dropdown.Menu>
              </Dropdown>
            </ButtonToolbar>
          </Col>
        </Row>
      )}
      <Row className="justify-content-end mt-3">
        <Col>
          {onDesktop && (
            <div className={styles.pendingAdminAplications}>
              <FontAwesomeIcon icon={faBooks} />
              {tTable(pendingTranslate, { count: pendings })}
            </div>
          )}
        </Col>
        <Col xs={12} md={4} className="d-flex">
          <Input.Search
            placeholder={t('home.lmsIndex.search.placeholder')}
            className={styles.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FastActions
            fastActions={fastActions}
            label={t(`home.myTeam.filters.default`)}
            fastActionSelected={selected}
            onFastActionSelected={(value) => {
              setSelected(value)
            }}
          />
          {!onDesktop && (
            <ButtonToolbar className={`ml-2 ${styles.buttons}`}>
              <Dropdown id="dropdown-manage" className={styles.dropdownManage}>
                <Dropdown.Toggle
                  id="dropdown-manage-toggle"
                  bsPrefix="noCaret"
                  className={`${!onDesktop && styles.responsiveDropdown}`}
                >
                  {!onDesktop ? (
                    <FontAwesomeIcon icon={faEllipsisV} className="ml-0" />
                  ) : (
                    t('administration.courses.list.dropdown.actions')
                  )}
                  <FontAwesomeIcon icon={faChevronDown} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <DownloadReport
                    type={TypesRequests.applications}
                    typeCourse={selected}
                  />
                </Dropdown.Menu>
              </Dropdown>
            </ButtonToolbar>
          )}
        </Col>
      </Row>
      <Table className={`table-basic ${styles.table}`} size="sm">
        <thead>
          {!onDesktop && (
            <tr className={styles.pendings}>
              <td className={styles.noBorder}>
                <div className={styles.pendingAdminAplications}>
                  <FontAwesomeIcon icon={faBooks} />
                  {tTable(pendingTranslate, { count: pendings })}
                </div>
              </td>
            </tr>
          )}
          <tr className={styles.tableHeader}>
            <th>{tTable('header.name')}</th>
            {onDesktop && (
              <>
                <th className="type">{tTable('header.type')}</th>
                <th>{tTable('header.course')}</th>
                <th>{tTable('header.state')}</th>
                <th>{tTable('header.stateAdmin')}</th>
              </>
            )}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const modal = (
              <td>
                {item.type === CourseTypes.LMS && (
                  <ModalDetailsLMS enrol={item.data} isAdmin={isAdmin} />
                )}
                {item.type === CourseTypes.DNC && (
                  <ModalDetailsDNC
                    requestCourse={item.data}
                    isAdmin={isAdmin}
                  />
                )}
              </td>
            )
            return (
              <tr key={[item.type, item.id].join('-')}>
                {onDesktop ? (
                  <>
                    <td>
                      <UserAvatar
                        user={item.user}
                        className={styles.avatarText}
                      />
                    </td>
                    <td>
                      {t(
                        joinKeys(
                          prefix,
                          'typeRequests',
                          item.type === CourseTypes.DNC ? 'DNC' : 'LMS'
                        )
                      )}
                    </td>
                    <td>{item.course}</td>
                    <td>
                      <StateEnrol className={styles.state} state={item.state} />
                    </td>
                    <td>
                      <StateEnrol
                        className={styles.state}
                        state={item.stateAdmin}
                      />
                    </td>
                    {modal}
                  </>
                ) : (
                  <>
                    <td>
                      <Avatar
                        src={
                          item.user.avatar
                            ? item.user.avatar
                            : '../assets/avatar-loading-xxl.svg'
                        }
                        sizes={AvatarSizes.Small}
                        className={styles.img}
                      />
                      <div className="d-inline-block">
                        <p className={styles.name}>
                          {' '}
                          {item.user.fullName
                            ? item.user.fullName
                            : `${item.user.name} ${item.user.lastname}`}
                        </p>
                        {item.state === EnrolState.Denied ? (
                          t('home.myTeam.table.deniedStateAdmin')
                        ) : (
                          <StateEnrol state={item.stateAdmin} />
                        )}
                      </div>
                    </td>
                    {modal}
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </ContentContainer>
  )
}

export default ApplicationsTable
